// @flow
import { 
  AppBar,
  Box,
  Button,
  Container, 
  CssBaseline, 
  Toolbar,
  Typography 
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Build as DraftIcon,
  HourglassEmpty as ValidatingIcon,
  NavigateNext as NextIcon,
  WhatsApp as SupportIcon,
  Work as ProcessingIcon
} from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router-dom';
import routes from '../routes';
import ProcessAgreementData from '../component/ProcessAgreementData';
import ProcessVideoData from '../component/ProcessVideoData';
import { AuthContext, setAuthToken } from '../context/auth';
import SignProcess, { ProcessResponse } from '../model/SignProcess';
import { toSentence } from '../util/string';
import { whatsappURL } from '../util/vendor';
import ApiResponse from '../model/ApiResponse';
import type { AgreementProps } from '../model/props';

const videoData = 'video-data';
const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(2),
  },
  appBarContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  logo: {
    height: 32,
    marginRight: theme.spacing(2),
  },
  brandContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
  },
  brandText: {
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  poweredBy: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    marginLeft: 'auto',
  },
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  validating: {
    color: theme.palette.grey[ '700' ],
  },
  processing: {
    color: theme.palette.grey[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error[ '600' ],
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(14),
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  bottomButton: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  nextStepButton: {
    background: 'linear-gradient(45deg, #2196F3 30%, #021c49 90%)',
    border: 0,
    borderRadius: 25,
    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
    color: 'white',
    height: 56,
    padding: '0 30px',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    textTransform: 'none',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      background: 'linear-gradient(45deg, #1976D2 30%, #021c49 90%)',
      boxShadow: '0 6px 10px 4px rgba(33, 203, 243, .3)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  nextStepContainer: {
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    textAlign: 'center',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e0e0e0',
  },
  nextStepText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontWeight: 500,
  },
  nextStepSubtext: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.95rem',
  },
  pulseAnimation: {
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.7)',
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(33, 150, 243, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(33, 150, 243, 0)',
    },
  },
  supportContainer: {
    background: 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0),
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    border: '1px solid #c8e6c9',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
    },
  },
  supportButton: {
    background: 'linear-gradient(45deg, #4CAF50 30%, #2E7D32 90%)',
    color: 'white',
    borderRadius: 20,
    padding: theme.spacing(1.5, 3),
    fontSize: '1rem',
    fontWeight: '500',
    textTransform: 'none',
    boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      background: 'linear-gradient(45deg, #45a049 30%, #1B5E20 90%)',
      boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)',
      transform: 'translateY(-1px)',
    },
  },
  supportIcon: {
    color: 'white',
    marginRight: theme.spacing(1),
  },
  supportText: {
    color: 'white',
    fontWeight: '500',
  },
  supportTitle: {
    color: theme.palette.text.primary,
    fontWeight: '600',
    marginBottom: theme.spacing(1),
  },
}));

type Props = {
  isRetailClient: boolean,
  match?: any,
};

const baseUrl = process.env.PUBLIC_URL || '';

const Contract = (props: Props) => {
  const { user, doGet } = useContext(AuthContext);
  const { t } = useTranslation();
  const history = useHistory();
  const { match, isRetailClient } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [supportPhone, setSupportPhone] = useState<?string>();
  const [agentPhone, setAgentPhone] = useState<?string>();
  const [process, setProcess] = useState<?SignProcess>();
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<?string>();
  // const [expanded, setExpanded] = useState<?string>();

  const loadData = () => {
    if (!process
      && match && match.params
      && match.params.code && match.params.token) {
      let path = 'retail';
      setAuthToken(null);
      if (!isRetailClient) {
        setAuthToken(match.params.token);
        path = 'token';
      }
      doGet(`/customer/${path}-sign-process/${match.params.code}/${match.params.token}`)
        .then((response: ApiResponse) => {
          if (response.ok) {
            setProcess(SignProcess.parse(response.data));
          } else {
            setError(response.message || 'Error');
          }
        });
      if (!supportPhone) {
        doGet('/customer/support-phone')
          .then((response: ApiResponse) => setSupportPhone(response.data));
      }
      if (!agentPhone) {
        doGet('/customer/agent-phone')
          .then((response: ApiResponse) => setAgentPhone(response.data));
      }
    }
  };

  useEffect(loadData, []);

  const saveAgreementData = (data: AgreementProps): Promise<boolean> => {
    let token = null;
    if (!!process && !process.hasContractAgreementApproved) {
      let theData = data;
      if (match && match.params && match.params.token) {
        token = match.params.token;
      }
      setProcessing(true);
      return new Promise(resolve => {
        process.saveAgreement({...theData, token})
          .then((response: ProcessResponse) => {
            if (response.ok) {
              setProcess(response.process);
              enqueueSnackbar(t('Contract agreement saved'), {
                variant: 'success',
              });
            } else if (!!response.message) {
              enqueueSnackbar(t(response.message), {
                variant: 'error',
              });
            }
            setProcessing(false);
            resolve(response.ok);
          });
      });
    }
    return new Promise<boolean>(resolve => resolve(false));
  };

    // Add handleExpanded function
  const handleExpanded = (key: string) => (event, isExpanded: boolean) => {
    // setExpanded(isExpanded ? key : null);
  };

  // Add saveVideoData function
  const saveVideoData = ({ file }): Promise<boolean> => {
    if (!!process) {
      setProcessing(true);
      return new Promise<boolean>(resolve => {
        process.saveVideo(file)
          .then((response: ApiResponse) => {
            if (response.ok) {
              let theProcess = SignProcess.parse(response.data);
              setProcess(theProcess);
              enqueueSnackbar(t('Verification saved'), {
                variant: 'success'
              });
              enqueueSnackbar(t('Your process is VIDEO_LOADED'), {
                variant: 'success'
              });
              // setExpanded(null);
            } else if (!response.is401Error()) {
              enqueueSnackbar(t(response.message), {
                variant: 'error'
              });
            }
            setProcessing(false);
            resolve(response.ok);
          });
      });
    }
    return new Promise<boolean>(resolve => resolve(false));
  };

  if (!match || !match.params || !match.params.code) {
    return <Redirect to={routes.home}/>;
  }

  let contractAgreementUrl = '';
  let templateAgreementUrl = '';
  if (!!process) {
    contractAgreementUrl = process.getContractAgreementUrl(match.params.token);
    templateAgreementUrl = process.getTemplateAgreementUrl(match.params.token);
  }

  return (
    <Container component="main" maxWidth="xl">
      <CssBaseline/>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Box className={classes.appBarContent}>
            <div className={classes.brandContainer}>
              <img 
                src={`${baseUrl}/images/logo.png`} 
                alt="Logo" 
                className={classes.logo}
              />
              <div>
                <Typography variant="h6" className={classes.brandText}>
                  {t('Contract Agreement')}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t('EcuaFirma Digital Signature Process')}
                </Typography>
              </div>
            </div>
            <Typography variant="caption" className={classes.poweredBy}>
              {t('Powered by eQon')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <div className={classes.paper}>
        {(!process && !error) && [1, 2, 3, 4, 5].map((index) => (
          <Skeleton key={index}/>
        ))}
        {!!error && (
          <>
            <Typography variant="subtitle2" className={classes.error}>
              {t(error)}
            </Typography>
          </>
        )}
        {!!process && (
          <>
            <Typography variant="h5">
              {process.isDraft()
              && <DraftIcon className={classes.draft}/>}
              {(process.hasVideoLoaded() && ! process.isProcessing() && !process.isCompleted())
              && <ValidatingIcon className={classes.draft}/>}
              {(process.isProcessing() || process.isCompleted())
              && <ProcessingIcon className={classes.processing}/>}
              &nbsp;{t(toSentence(process.processType))} <small>({t(`Your process is ${process.state}`)})</small>
            </Typography>
            <Typography variant="subtitle2" className={classes.grey}>
              {t('Duration')}: {process.durationValue()} {t(process.durationLabel()).toLowerCase()}.
            </Typography>
            <Typography variant="subtitle2" className={classes.grey}>
              {t('Price')}: ${process.price.toFixed(2)}.
            </Typography>
            <small>&nbsp;</small>
            <ProcessAgreementData
              isRetailClient={isRetailClient}
              isValidation={false}
              code="agreement-data"
              disabled={processing || process.hasDocumentsValidated()}
              processing={processing}
              onSave={saveAgreementData}
              contractAgreementUrl={contractAgreementUrl}
              templateAgreementUrl={templateAgreementUrl}
              expanded
              onExpanded={(key) => handleExpanded(key, true)}
              process={process}/>

            {/* Video data */} 
           <ProcessVideoData
            code={videoData}
            disabled={processing || process.hasDocumentsValidated()}
            processing={processing}
            onSave={saveVideoData}
            expanded
            onExpanded={(key) => handleExpanded(key, true)}
            process={process} />
          
          </>
        )}
      </div>

      {/* Continue Process Button */}
      {(!!process && !!process.hasAgreementLoaded()) && (
        <Box className={classes.bottomButton}>
          <div className={classes.nextStepContainer}>
            <Typography variant="h6" className={classes.nextStepText}>
              {t('Ready to continue?')}
            </Typography>
            <Typography variant="body2" className={classes.nextStepSubtext}>
              {t('Complete your digital signature process in the next step')}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              startIcon={<NextIcon />}
              className={`${classes.nextStepButton} ${classes.pulseAnimation}`}
              onClick={() => {
                history.push(`/process/${process?.code || ''}`);
              }}
            >
              {t('Continue Process')}
            </Button>
          </div>
        </Box>
      )}

      {/* Agent Support */}
             <Box className={classes.supportContainer}>
               <Typography variant="subtitle1" className={classes.supportTitle}>
                 {t('Need technical support?')}
               </Typography>
               <Button
                 fullWidth
                 startIcon={<SupportIcon className={classes.supportIcon} />}
                 className={classes.supportButton}
                 onClick={() => {
                   const url = whatsappURL(agentPhone, t('Support text', user));
                   window.open(url, '_blank');
                 }}
               >
                 <span className={classes.supportText}>{t('Contact Support via WhatsApp')}</span>
               </Button>
             </Box>

    </Container>
  );
};

export default Contract;
